import { extract } from './extract-util.js';
import { globalSprinkles } from './safe-sprinkles.js';
import {
  AlignContentPositionProps,
  AlignItemsPositionProps,
  AlignSelfPositionProps,
  JustifyContentPositionProps,
  JustifyItemsPositionProps,
  JustifySelfPositionProps,
  PlaceContentPositionProps,
  PlaceItemsPositionProps,
  PlaceSelfPositionProps,
} from './sprinkle-properties.js';

/**
 * Defines the grid flex positions props.
 * @public
 */
export interface GridFlexPositionProps {
  /** justify self positions */
  justifySelf?: JustifySelfPositionProps;
  /** align self positions */
  alignSelf?: AlignSelfPositionProps;
  /** place self positions */
  placeSelf?: PlaceSelfPositionProps;
  /** justify items positions */
  justifyItems?: JustifyItemsPositionProps;
  /** align items positions */
  alignItems?: AlignItemsPositionProps;
  /** place items positions */
  placeItems?: PlaceItemsPositionProps;
  /** justify content positions */
  justifyContent?: JustifyContentPositionProps;
  /** align content positions */
  alignContent?: AlignContentPositionProps;
  /** place content positions */
  placeContent?: PlaceContentPositionProps;
}

/**
 * Keys that will define the grid flex positions.
 * @public
 */
const gridFlexPositionPropKeys: Array<keyof GridFlexPositionProps> = [
  'justifySelf',
  'alignSelf',
  'placeSelf',
  'justifyItems',
  'alignItems',
  'placeItems',
  'justifyContent',
  'alignContent',
  'placeContent',
];

/**
 * Given a set of props, this function will extract the sprinkles props for
 * Grid/Flex Position spinkles and return the appropriate
 * classnames as well as the remaining props.
 * @internal
 * */
export function getGridFlexPositionSprinkles<T extends GridFlexPositionProps>(
  props: T,
  defaults: Partial<GridFlexPositionProps> = {},
): [
  sprinklesClassNames: string,
  remainingProps: Omit<T, keyof GridFlexPositionProps>,
] {
  const { extracted, rest } = extract(props, gridFlexPositionPropKeys);
  const { extracted: extractedDefaults } = extract(
    defaults,
    gridFlexPositionPropKeys,
  );

  return [globalSprinkles({ ...extractedDefaults, ...extracted }), rest];
}
